<script setup lang="ts">
import {computed, type Component} from "vue";
import {ArrowRight, Circle, CircleCheck, CircleDot} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import {course, CourseScene, SceneSummaryTone} from "@/medication-checkup/course";
import {MedicationCheckupBlock} from "@/medication-checkup/interfaces";
import {plannedLabel} from "@/medication-checkup/services/StepPlanning";
import {useNextStep} from "@/medication-checkup/composables/useNextStep";

/**
 * The card that keeps the step shown in view beside the thread: its name, and
 * one line per block telling where the interview stands.
 *
 * It shows nothing the thread does not already know — the blocks of the step
 * the thread shows, their completion as the HDS serves it, their labels as the
 * pinned definition declares them — laid out as a checklist rather than as a
 * conversation. A block already played is a place in the thread, so clicking it
 * asks the thread to scroll back to its scene. A step over hands the course
 * on: the way into the next step is a button of its own, since closing an
 * step leaves its record on screen rather than jumping ahead.
 */

const emit = defineEmits<{(event: 'jump', block: MedicationCheckupBlock): void}>();

const store = useMedicationCheckupStore();

/**
 * Where a block stands in the step: played and frozen, being played, or not
 * reached yet.
 */
type Standing = 'completed' | 'current' | 'upcoming';

/**
 * The tones a line can take: those of an outcome, plus the one of the block
 * being played, which has no outcome yet.
 */
type LineTone = SceneSummaryTone | 'current';

interface Line {
    label: string;
    tone: LineTone;
}

interface ChecklistItem {
    block: MedicationCheckupBlock;
    label: string;
    standing: Standing;
    /**
     * What the line says under the label: the outcome of a completed block
     * when its scene summarises one, the progress of the current one when
     * its scene tells it, otherwise where the block stands.
     */
    summary: Line;
    /**
     * What the scene of the current block shows under its line, if anything.
     */
    detail: Component | null;
}

const ICONS: Record<Standing, Component> = {
    completed: CircleCheck,
    current: CircleDot,
    upcoming: Circle
};

const ICON_CLASSES: Record<Standing, string> = {
    completed: 'text-secondary-darkest',
    current: 'text-primary',
    upcoming: 'text-gray'
};

/**
 * What a block says of itself when its scene has no outcome to report.
 */
const STANDING_LINES: Record<Standing, Line> = {
    completed: {label: 'Terminé', tone: 'good'},
    current: {label: 'En cours', tone: 'current'},
    upcoming: {label: 'À venir', tone: 'neutral'}
};

const TONE_CLASSES: Record<LineTone, string> = {
    neutral: 'text-muted-foreground',
    current: 'font-medium text-primary',
    good: 'font-semibold text-secondary-darkest',
    warning: 'font-semibold text-amber-800',
    alert: 'font-semibold text-red-700'
};

/**
 * The line under a label: a completed block reports its outcome, read by its
 * scene from the frozen view; the current one its progress, when its scene
 * tells it; any other block only says where it stands.
 */
const summarise = (block: MedicationCheckupBlock, standing: Standing, scene: CourseScene | undefined): Line => {
    if (standing === 'current') {
        return scene?.ongoing?.() ?? STANDING_LINES.current;
    }

    if (standing !== 'completed' || block.view === null || scene?.summary === undefined) {
        return STANDING_LINES[standing];
    }

    return scene.summary(block.view) ?? STANDING_LINES.completed;
};

/**
 * The step the card shows: the one the thread shows.
 */
const step = computed(() => store.displayedStep);

/**
 * Whether the step shown is over: the card then heads itself accordingly,
 * and offers the way into the next one.
 */
const {over, next, label: nextLabel, open: openNext} = useNextStep();

const stepLabel = computed(() => {
    if (step.value === null) {
        return null;
    }

    return store.declaredStep(step.value.definition_key)?.label ?? null;
});

/**
 * The blocks of the step, one line each, in course order.
 *
 * A block the definition does not declare is skipped, as the thread skips it; so
 * is one whose scene asks to stay out of the checklist. The current block is
 * the first one not completed — the same rule the thread plays by.
 */
/**
 * The appointment of the step shown, while it is still to hold.
 */
const planned = computed(() => step.value === null
    ? null
    : plannedLabel(step.value, store.declaredStep(step.value.definition_key)));

const items = computed<ChecklistItem[]>(() => {
    if (step.value === null) {
        return [];
    }

    const blocks = store.displayedBlocks;
    const current = blocks.find(block => !block.is_completed) ?? null;

    return blocks.flatMap(block => {
        const declaration = store.declaredBlock(block.definition_key);
        const scene = course.find(candidate => candidate.key === block.definition_key);

        if (declaration === null || scene?.checklist === false) {
            return [];
        }

        const standing: Standing = block.is_completed
            ? 'completed'
            : block === current ? 'current' : 'upcoming';

        return [{
            block,
            label: declaration.label,
            standing,
            summary: summarise(block, standing, scene),
            detail: standing === 'current' ? scene?.detail ?? null : null
        }];
    });
});

/**
 * Only a block the thread has played can be jumped to: an upcoming one has no
 * scene yet.
 */
const reachable = (item: ChecklistItem): boolean => item.standing !== 'upcoming';
</script>

<template>
  <div v-if="step" class="bg-white rounded-md shadow-custom px-3.5 py-3">
    <div class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
      {{ over ? 'Étape terminée' : 'Étape en cours' }}
    </div>
    <div class="mt-0.5 mb-2.5 text-[13px] font-semibold text-primary">
      {{ stepLabel }}
      <span
        v-if="planned"
        class="block text-[11px]"
        :class="planned.booked ? 'font-medium text-primary' : 'font-semibold text-amber-800'"
      >
        {{ planned.label }}
      </span>
    </div>

    <ul class="flex flex-col gap-2">
      <li v-for="item in items" :key="item.block.id">
        <button
          type="button"
          :disabled="!reachable(item)"
          class="flex w-full items-start gap-2 text-left transition-opacity duration-200
            enabled:cursor-pointer enabled:hover:opacity-75 disabled:cursor-default"
          @click="emit('jump', item.block)"
        >
          <component
            :is="ICONS[item.standing]"
            class="mt-px size-[17px] shrink-0"
            :class="ICON_CLASSES[item.standing]"
            :stroke-width="2.2"
          />
          <span class="flex min-w-0 flex-col gap-px">
            <span class="text-xs font-semibold text-gray-dark">{{ item.label }}</span>
            <span class="text-[11px]" :class="TONE_CLASSES[item.summary.tone]">
              {{ item.summary.label }}
            </span>
          </span>
        </button>
        <component :is="item.detail" v-if="item.detail" />
      </li>
    </ul>

    <Button
      v-if="next && nextLabel"
      variant="secondary"
      size="sm"
      class="mt-3 w-full h-auto whitespace-normal py-1.5 text-left"
      :disabled="store.stepLoading"
      @click="openNext"
    >
      <span class="flex-1">Passer à l'étape suivante : {{ nextLabel }}</span>
      <ArrowRight />
    </Button>
  </div>
</template>
